import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  Search,
  Swords,
  MapPin,
  Users,
  DollarSign,
  Target,
  Shield,
  AlertTriangle,
  MessageSquare,
  Zap,
  Building2,
  Plus,
  Pencil,
  Trash2,
} from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { useDebounce } from '@/hooks/use-debounce'
import { useUser } from '@/contexts/UserContext'
import { useToast } from '@/hooks/use-toast'
import { CATEGORICAL } from '@/lib/theme'

interface Battlecard {
  id: string
  competitor_name: string
  product_details?: string
  domain?: string
  company_size_people?: string
  company_size_value?: string
  head_office?: string
  their_competitors?: string
  our_gaps?: string
  core_competing_offering?: string
  our_strengths?: string
  their_strengths?: string
  landmine_questions?: string
  counter_pitch?: string
  pricing?: string
}

export default function Battlecards() {
  const { isAdmin } = useUser()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const [selectedCard, setSelectedCard] = useState<Battlecard | null>(null)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editForm, setEditForm] = useState<Partial<Battlecard>>({})
  const [isSaving, setIsSaving] = useState(false)

  const handleEdit = (card: Battlecard) => {
    setEditForm({ ...card })
    setEditModalOpen(true)
    setSelectedCard(null)
  }

  const handleCreate = () => {
    setEditForm({ competitor_name: '' })
    setEditModalOpen(true)
  }

  const handleSave = async () => {
    if (!editForm.competitor_name?.trim()) {
      toast({ title: 'Error', description: 'Competitor name is required', variant: 'destructive' })
      return
    }
    setIsSaving(true)
    try {
      if (editForm.id) {
        await (apiClient as any).client.put(`/battlecards/${editForm.id}`, editForm)
        toast({ title: 'Battlecard Updated' })
      } else {
        await (apiClient as any).client.post('/battlecards', editForm)
        toast({ title: 'Battlecard Created' })
      }
      queryClient.invalidateQueries({ queryKey: ['battlecards'] })
      setEditModalOpen(false)
    } catch {
      toast({ title: 'Error', description: 'Failed to save battlecard', variant: 'destructive' })
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (card: Battlecard) => {
    if (!window.confirm(`Delete battlecard for "${card.competitor_name}"?`)) return
    try {
      await (apiClient as any).client.delete(`/battlecards/${card.id}`)
      toast({ title: 'Battlecard Deleted' })
      queryClient.invalidateQueries({ queryKey: ['battlecards'] })
      setSelectedCard(null)
    } catch {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' })
    }
  }

  const { data, isLoading } = useQuery({
    queryKey: ['battlecards', debouncedSearch],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (debouncedSearch) params.set('search', debouncedSearch)
      const response = await (apiClient as any).client.get(`/battlecards?${params.toString()}`)
      return response.data?.data as Battlecard[]
    },
  })

  const battlecards = data || []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Swords className="h-6 w-6 text-primary" aria-hidden="true" />
            <h1 className="text-3xl font-bold tracking-tight">Battlecards</h1>
            <Badge variant="secondary">{battlecards.length}</Badge>
          </div>
          <p className="text-muted-foreground mt-1">
            Competitive intelligence for sales conversations
          </p>
        </div>
        {isAdmin && (
          <Button onClick={handleCreate} aria-label="Add new battlecard">
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            New Battlecard
          </Button>
        )}
      </div>

      <div className="relative max-w-md">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          placeholder="Search competitors, domains, offerings..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          aria-label="Search battlecards"
        />
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Loading battlecards...</div>
      ) : battlecards.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {search ? 'No competitors match your search' : 'No battlecards available'}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {battlecards.map((card) => (
            <Card
              key={card.id}
              className="cursor-pointer hover:shadow-md transition-shadow focus-within:ring-2 focus-within:ring-primary"
            >
              <button
                className="w-full text-left p-0 focus:outline-none"
                onClick={() => setSelectedCard(card)}
                aria-label={`View battlecard for ${card.competitor_name}`}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2
                      className="h-4 w-4 text-muted-foreground flex-shrink-0"
                      aria-hidden="true"
                    />
                    {card.competitor_name}
                  </CardTitle>
                  {card.domain && (
                    <Badge variant="outline" className="text-xs w-fit">
                      {card.domain}
                    </Badge>
                  )}
                </CardHeader>
                <CardContent className="space-y-2">
                  {card.core_competing_offering && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {card.core_competing_offering}
                    </p>
                  )}
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {card.company_size_people && (
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" aria-hidden="true" />
                        {card.company_size_people}
                      </span>
                    )}
                    {card.head_office && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" aria-hidden="true" />
                        {card.head_office.split(',')[0]}
                      </span>
                    )}
                  </div>
                </CardContent>
              </button>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={!!selectedCard} onOpenChange={() => setSelectedCard(null)}>
        <DialogContent
          className="max-w-3xl max-h-[85vh] overflow-y-auto"
          aria-describedby="battlecard-details"
        >
          {selectedCard && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle className="text-xl flex items-center gap-2">
                    <Swords className="h-5 w-5 text-primary" aria-hidden="true" />
                    {selectedCard.competitor_name}
                  </DialogTitle>
                  {isAdmin && (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(selectedCard)}
                        aria-label="Edit battlecard"
                      >
                        <Pencil className="h-4 w-4" aria-hidden="true" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(selectedCard)}
                        aria-label="Delete battlecard"
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {selectedCard.domain && <Badge variant="outline">{selectedCard.domain}</Badge>}
                  {selectedCard.company_size_people && (
                    <Badge variant="secondary">{selectedCard.company_size_people}</Badge>
                  )}
                  {selectedCard.company_size_value && (
                    <Badge variant="secondary">{selectedCard.company_size_value}</Badge>
                  )}
                </div>
              </DialogHeader>

              <div id="battlecard-details" className="space-y-5">
                {/* Overview */}
                {selectedCard.product_details && (
                  <Section icon={Building2} title="Overview">
                    <p className="text-sm whitespace-pre-wrap">{selectedCard.product_details}</p>
                    {selectedCard.head_office && (
                      <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
                        <MapPin className="h-3 w-3" aria-hidden="true" /> {selectedCard.head_office}
                      </p>
                    )}
                  </Section>
                )}

                {/* Why We Win */}
                {selectedCard.our_strengths && (
                  <Section
                    icon={Target}
                    title="Why We Win"
                    style={{ color: CATEGORICAL.evergreen.solid }}
                  >
                    <p className="text-sm whitespace-pre-wrap">{selectedCard.our_strengths}</p>
                  </Section>
                )}

                {/* Watch Out — Their Strengths */}
                {selectedCard.their_strengths && (
                  <Section
                    icon={AlertTriangle}
                    title="Watch Out — Their Strengths"
                    style={{ color: CATEGORICAL.clay.solid }}
                  >
                    <p className="text-sm whitespace-pre-wrap">{selectedCard.their_strengths}</p>
                  </Section>
                )}

                {/* Our Gaps */}
                {selectedCard.our_gaps && (
                  <Section
                    icon={Shield}
                    title="Our Gaps vs. Them"
                    style={{ color: CATEGORICAL.raspberry.solid }}
                  >
                    <p className="text-sm whitespace-pre-wrap">{selectedCard.our_gaps}</p>
                  </Section>
                )}

                {/* Landmine Questions */}
                {selectedCard.landmine_questions && (
                  <Section
                    icon={MessageSquare}
                    title="Landmine Questions to Ask"
                    style={{ color: CATEGORICAL.teal.solid }}
                  >
                    <div
                      className="rounded-lg p-4"
                      style={{
                        backgroundColor: CATEGORICAL.teal.tint,
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        borderColor: CATEGORICAL.teal.solid,
                      }}
                    >
                      <p
                        className="text-sm font-medium whitespace-pre-wrap"
                        style={{ color: CATEGORICAL.prussian.solid }}
                      >
                        {selectedCard.landmine_questions}
                      </p>
                    </div>
                  </Section>
                )}

                {/* Sales Judo */}
                {selectedCard.counter_pitch && (
                  <Section
                    icon={Zap}
                    title="How to Counter Their Pitch"
                    style={{ color: CATEGORICAL.plum.solid }}
                  >
                    <div
                      className="rounded-lg p-4"
                      style={{
                        backgroundColor: CATEGORICAL.plum.tint,
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        borderColor: CATEGORICAL.plum.solid,
                      }}
                    >
                      <p
                        className="text-sm whitespace-pre-wrap"
                        style={{ color: CATEGORICAL.prussian.solid }}
                      >
                        {selectedCard.counter_pitch}
                      </p>
                    </div>
                  </Section>
                )}

                {/* Pricing */}
                {selectedCard.pricing && (
                  <Section
                    icon={DollarSign}
                    title="Pricing"
                    style={{ color: CATEGORICAL.evergreen.solid }}
                  >
                    <p className="text-sm whitespace-pre-wrap">{selectedCard.pricing}</p>
                  </Section>
                )}

                {/* Their Competitors */}
                {selectedCard.their_competitors && (
                  <Section icon={Users} title="They Also Compete With">
                    <p className="text-sm">{selectedCard.their_competitors}</p>
                  </Section>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit/Create Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent
          className="max-w-3xl max-h-[85vh] overflow-y-auto"
          aria-describedby="edit-battlecard-desc"
        >
          <DialogHeader>
            <DialogTitle>{editForm.id ? 'Edit Battlecard' : 'New Battlecard'}</DialogTitle>
            <DialogDescription id="edit-battlecard-desc">
              {editForm.id ? 'Update competitor information' : 'Add a new competitor battlecard'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bc-name">Competitor Name *</Label>
                <Input
                  id="bc-name"
                  value={editForm.competitor_name || ''}
                  onChange={(e) => setEditForm((p) => ({ ...p, competitor_name: e.target.value }))}
                  aria-required="true"
                />
              </div>
              <div>
                <Label htmlFor="bc-domain">Domain</Label>
                <Input
                  id="bc-domain"
                  value={editForm.domain || ''}
                  onChange={(e) => setEditForm((p) => ({ ...p, domain: e.target.value }))}
                  placeholder="e.g. Software, Consulting"
                />
              </div>
              <div>
                <Label htmlFor="bc-size-people">Company Size (People)</Label>
                <Input
                  id="bc-size-people"
                  value={editForm.company_size_people || ''}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, company_size_people: e.target.value }))
                  }
                  placeholder="e.g. 51-200"
                />
              </div>
              <div>
                <Label htmlFor="bc-size-value">Company Size ($ Value)</Label>
                <Input
                  id="bc-size-value"
                  value={editForm.company_size_value || ''}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, company_size_value: e.target.value }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="bc-hq">Head Office</Label>
                <Input
                  id="bc-hq"
                  value={editForm.head_office || ''}
                  onChange={(e) => setEditForm((p) => ({ ...p, head_office: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="bc-pricing">Pricing</Label>
                <Input
                  id="bc-pricing"
                  value={editForm.pricing || ''}
                  onChange={(e) => setEditForm((p) => ({ ...p, pricing: e.target.value }))}
                />
              </div>
            </div>
            {(
              [
                ['product_details', 'Product Details & Relationship'],
                ['core_competing_offering', 'Core Competing Offering'],
                ['our_strengths', 'Our Strengths (Why We Win)'],
                ['their_strengths', 'Their Strengths (Potential Traps)'],
                ['our_gaps', 'Our Gaps vs. Them'],
                ['landmine_questions', 'Landmine Questions to Ask'],
                ['counter_pitch', 'How to Counter Their Pitch'],
                ['their_competitors', 'Their Competitors'],
              ] as const
            ).map(([field, label]) => (
              <div key={field}>
                <Label htmlFor={`bc-${field}`}>{label}</Label>
                <textarea
                  id={`bc-${field}`}
                  value={(editForm as any)[field] || ''}
                  onChange={(e) => setEditForm((p) => ({ ...p, [field]: e.target.value }))}
                  className="mt-1 flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={label}
                />
              </div>
            ))}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setEditModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : editForm.id ? 'Save Changes' : 'Create Battlecard'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function Section({
  icon: Icon,
  title,
  style,
  children,
}: {
  icon: any
  title: string
  style?: React.CSSProperties
  children: React.ReactNode
}) {
  return (
    <div>
      <h3 className="text-sm font-semibold flex items-center gap-2 mb-2" style={style}>
        <Icon className="h-4 w-4" aria-hidden="true" />
        {title}
      </h3>
      {children}
    </div>
  )
}
